import type {
  ChatAudienceKind,
  IChatAudienceOption,
  IChatAudiences,
  IChatContact,
  IChatUserSummary,
} from "@/types/(my-space)/chat.interface";

/**
 * A member picker's selection: who is in it, and why. Every selected person
 * carries the sources that put them there — `MANUAL` for a row picked in the
 * contact list, or the key of an "Add by" audience. Removing an audience drops
 * only the people no other source still holds, so "Engineering · 18" never
 * takes away someone picked by hand or brought in by another audience.
 */
export const MANUAL_SOURCE = "manual";

/** The per-request cap of the create / add-members endpoints. */
export const MAX_CHAT_MEMBERS = 2000;

/** Above this the selected-people summary collapses to a count. */
export const COLLAPSE_SELECTION_AT = 50;

export type AudienceTab = "people" | ChatAudienceKind;

export interface AppliedAudience {
  key: string;
  kind: ChatAudienceKind;
  /** The entity names as applied (Company has none). */
  names: string[];
}

export interface PeopleSelection {
  /** In the order they were selected. */
  people: IChatContact[];
  sources: Record<number, string[]>;
  audiences: AppliedAudience[];
}

export const emptySelection = (): PeopleSelection => ({ people: [], sources: {}, audiences: [] });

/** The same audience applied twice is one chip. */
export const audienceKey = (kind: ChatAudienceKind, ids: number[] = []) =>
  `${kind}:${[...ids].sort((a, b) => a - b).join(",")}`;

/** Audience members carry no position/department; contact rows do. */
export const toContact = (u: IChatUserSummary): IChatContact => ({
  email: null,
  position: null,
  department: null,
  ...u,
});

export const isSelected = (sel: PeopleSelection, id: number) => !!sel.sources[id]?.length;

/** Live count of selected people a given audience still accounts for. */
export const audienceCount = (sel: PeopleSelection, key: string) =>
  sel.people.reduce((n, p) => (sel.sources[p.id]?.includes(key) ? n + 1 : n), 0);

/** Drops audience chips nobody is attributed to any more. */
const pruneAudiences = (sel: PeopleSelection): PeopleSelection => {
  const live = new Set(Object.values(sel.sources).flat());
  const audiences = sel.audiences.filter((a) => live.has(a.key));
  return audiences.length === sel.audiences.length ? sel : { ...sel, audiences };
};

/** A contact-list click: selects by hand, or removes the person entirely. */
export const togglePerson = (sel: PeopleSelection, person: IChatContact): PeopleSelection => {
  if (isSelected(sel, person.id)) return removePerson(sel, person.id);
  return {
    ...sel,
    people: [...sel.people, person],
    sources: { ...sel.sources, [person.id]: [MANUAL_SOURCE] },
  };
};

/** Removing a chip removes the person whatever brought them in. */
export const removePerson = (sel: PeopleSelection, id: number): PeopleSelection => {
  if (!isSelected(sel, id)) return sel;
  const sources = { ...sel.sources };
  delete sources[id];
  return pruneAudiences({ ...sel, people: sel.people.filter((p) => p.id !== id), sources });
};

/**
 * Unions an audience's members into the selection. `excludeIds` (me, people
 * already in the chat) are never added. Returns how many were newly selected.
 */
export const addAudience = (
  sel: PeopleSelection,
  audience: AppliedAudience,
  members: IChatUserSummary[],
  excludeIds: Iterable<number> = [],
): { selection: PeopleSelection; added: number } => {
  const exclude = new Set(excludeIds);
  const sources = { ...sel.sources };
  const people = [...sel.people];
  let added = 0;
  let attributed = 0;
  for (const m of members) {
    if (exclude.has(m.id)) continue;
    const current = sources[m.id];
    // Already selected — including a duplicate earlier in this same batch.
    if (current?.length) {
      if (!current.includes(audience.key)) sources[m.id] = [...current, audience.key];
      attributed++;
      continue;
    }
    sources[m.id] = [audience.key];
    people.push(toContact(m));
    added++;
    attributed++;
  }
  const audiences =
    attributed === 0 || sel.audiences.some((a) => a.key === audience.key)
      ? sel.audiences
      : [...sel.audiences, audience];
  return { selection: { people, sources, audiences }, added };
};

/** Undoes an audience: drops it as a source, and whoever it alone held. */
export const removeAudience = (sel: PeopleSelection, key: string): PeopleSelection => {
  const sources: Record<number, string[]> = {};
  const people: IChatContact[] = [];
  for (const p of sel.people) {
    const rest = (sel.sources[p.id] ?? []).filter((s) => s !== key);
    if (!rest.length) continue;
    sources[p.id] = rest;
    people.push(p);
  }
  return { people, sources, audiences: sel.audiences.filter((a) => a.key !== key) };
};

/**
 * Entities of [kind] already added through a chip (read back from the chip
 * keys). An added branch or team leaves the list until its chip is removed,
 * rather than offering a pick that would only say "already selected".
 */
export const addedEntityIds = (
  sel: PeopleSelection | undefined,
  kind: ChatAudienceKind,
): Set<number> => {
  const ids = new Set<number>();
  for (const a of sel?.audiences ?? []) {
    if (a.kind !== kind) continue;
    for (const part of a.key.slice(kind.length + 1).split(",")) {
      if (part) ids.add(Number(part));
    }
  }
  return ids;
};

const companyAdded = (sel: PeopleSelection | undefined) =>
  !!sel?.audiences.some((a) => a.kind === "company");

/**
 * Everyone it would add is already there: selected (by hand or another
 * audience) or, when adding to a chat, already a member. Picking the whole
 * company covers every branch, department, team and project at once.
 */
const covered = (
  memberIds: number[] | undefined,
  sel: PeopleSelection | undefined,
  excludeIds: Iterable<number> = [],
) => !!memberIds?.length && newPeople(memberIds, sel, excludeIds).length === 0;

/**
 * Who of [memberIds] picking it would actually add: not selected yet and not
 * already in the chat. The lists show this, not the entity's size — a branch
 * of 27 with one person left to add reads "1".
 */
export const newPeople = (
  memberIds: number[],
  sel: PeopleSelection | undefined,
  excludeIds: Iterable<number> = [],
): number[] => {
  const have = new Set(excludeIds);
  for (const p of sel?.people ?? []) have.add(p.id);
  return memberIds.filter((id) => !have.has(id));
};

/** People several picked entities would add together (overlaps counted once). */
export const newPeopleCount = (
  options: Pick<IChatAudienceOption, "memberIds">[],
  sel: PeopleSelection | undefined,
  excludeIds: Iterable<number> = [],
): number =>
  new Set(options.flatMap((o) => newPeople(o.memberIds ?? [], sel, excludeIds))).size;

/**
 * The "Add by" options, in order, for what the server offered — minus a kind
 * with nothing left to add (every entity added, or all its people selected).
 */
export const audienceTabs = (
  audiences: IChatAudiences | undefined | null,
  sel?: PeopleSelection,
  excludeIds: Iterable<number> = [],
): AudienceTab[] => {
  const tabs: AudienceTab[] = ["people"];
  if (!audiences) return tabs;
  const exclude = [...excludeIds];
  if (
    audiences.company &&
    audiences.company.memberCount > 0 &&
    !companyAdded(sel) &&
    !covered(audiences.company.memberIds, sel, exclude)
  ) {
    tabs.push("company");
  }
  for (const kind of ["branch", "department", "team", "project"] as const) {
    if (remainingAudienceOptions(audiences, kind, sel, exclude).length) tabs.push(kind);
  }
  return tabs;
};

/** The entity list behind one tab. */
export const audienceOptions = (
  audiences: IChatAudiences | undefined,
  kind: Exclude<ChatAudienceKind, "company">,
): IChatAudienceOption[] => {
  if (!audiences) return [];
  switch (kind) {
    case "branch":
      return audiences.branches;
    case "department":
      return audiences.departments;
    case "team":
      return audiences.teams;
    case "project":
      return audiences.projects;
  }
};

/**
 * The entity list behind one tab, without the ones already added or whose
 * people are all selected already.
 */
export const remainingAudienceOptions = (
  audiences: IChatAudiences | undefined,
  kind: Exclude<ChatAudienceKind, "company">,
  sel?: PeopleSelection,
  excludeIds: Iterable<number> = [],
): IChatAudienceOption[] => {
  const added = addedEntityIds(sel, kind);
  const exclude = [...excludeIds];
  return (
    audienceOptions(audiences, kind)
      .filter((o) => !added.has(o.id) && !covered(o.memberIds, sel, exclude))
      // memberCount becomes "how many it would add".
      .map((o) =>
        o.memberIds?.length
          ? { ...o, memberCount: newPeople(o.memberIds, sel, exclude).length }
          : o,
      )
  );
};

/** Case-, accent- and harakat-insensitive name match. */
const fold = (s: string) =>
  s
    .normalize("NFKD")
    .replace(/[̀-ًͯ-ٰٟ]/g, "")
    .toLowerCase()
    .trim();

export const filterAudienceOptions = (options: IChatAudienceOption[], search: string) => {
  const q = fold(search);
  return q ? options.filter((o) => fold(o.name).includes(q)) : options;
};
